<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" @close="closeModal" class="relative z-50">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/50" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              class="w-full max-w-lg transform overflow-hidden rounded-lg bg-white text-left align-middle shadow-xl transition-all flex flex-col max-h-[90vh]"
            >
              <div class="flex items-center justify-between px-6 py-4">
                <h3 class="text-lg font-semibold text-gray-900">
                  {{ $t('myMap.export.title') }}
                </h3>
                <button
                  @click="closeModal"
                  class="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100 cursor-pointer"
                >
                  <Icon icon="mdi:close" class="w-6 h-6" />
                </button>
              </div>

              <div class="p-6 overflow-y-auto">
                <form @submit.prevent="submitForm" id="map-export-form" class="flex flex-col gap-4">
                  <BaseInput
                    id="pdfName"
                    :label="$t('myMap.export.form.title.label')"
                    v-model="form.title.value.value"
                    :showErrorMessage="true"
                    :error="form.title.errorMessage.value"
                    @blur="form.title.handleChange"
                    :placeholder="$t('myMap.export.form.title.placeholder')"
                    width="w-full"
                  />

                  <div>
                    <label class="block font-medium text-gray-700 mb-2" for="export_description">
                      {{ $t('myMap.export.form.description.label') }}
                    </label>
                    <textarea
                      id="export_description"
                      v-model="form.description.value.value"
                      :placeholder="$t('myMap.export.form.description.placeholder')"
                      @blur="form.description.handleChange"
                      rows="4"
                      class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-main focus:outline-none focus:ring-1 focus:ring-main disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500 transition-colors"
                      :class="{ 'border-red-500': form.description.errorMessage.value }"
                    ></textarea>
                    <span
                      v-if="form.description.errorMessage.value"
                      class="text-xs text-red-500 mt-1"
                    >
                      {{ form.description.errorMessage.value }}
                    </span>
                  </div>
                </form>
              </div>

              <div class="px-6 py-4 flex justify-between items-center border-gray-200 gap-4">
                <button
                  type="button"
                  class="flex-1 cursor-pointer text-sm font-medium text-gray-600 hover:text-gray-900 border border-gray-300 py-2.5 rounded-md hover:bg-gray-100 transition-colors text-center"
                  @click="closeModal"
                >
                  {{ $t('forms.cancel') }}
                </button>

                <button
                  type="submit"
                  form="map-export-form"
                  :disabled="isSubmitting"
                  class="flex-1 inline-flex justify-center rounded-md bg-main hover:bg-main-hover py-2.5 text-sm font-medium text-white focus:outline-none cursor-pointer disabled:opacity-50 disabled:cursor-default transition-colors items-center"
                >
                  <Icon v-if="isSubmitting" icon="eos-icons:loading" class="mr-2 h-4 w-4" />
                  {{ $t('myMap.export.form.submit') }}
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { TransitionRoot, TransitionChild, Dialog, DialogPanel } from '@headlessui/vue'
import BaseInput from '@/components/ui/BaseInput.vue'
import { Icon } from '@iconify/vue'
import { apiBaseUrl } from '@/config/env'
import { LayerType } from '@/models/enums/geo/LayerType'
import { onInvalidSubmit } from '@/services/forms/FormService'
import { MapExportService } from '@/services/map/MapExportService'
import { fetchImageAsBase64 } from '@/services/utils/UtilsService'
import { useMyMapStore } from '@/stores/myMapStore'

const mapStore = useMyMapStore()
const { form, handleSubmit, isSubmitting } = MapExportService.getExportForm()

const isOpen = computed(() => mapStore.isMapExportActive)

const closeModal = () => {
  mapStore.isMapExportActive = false
}

const submitForm = handleSubmit(
  async (values) => {
    const legendToPrint = await Promise.all(
      mapStore.legendList.map(async (item) => {
        if (item.layerType === LayerType.APP_LAYER) {
          return {
            ...item,
            icon: await fetchImageAsBase64(item.icon),
          }
        }
        return item
      }),
    )

    const logo = await fetchImageAsBase64('/img/Logo.png')
    const footer = await fetchImageAsBase64('/img/pdf_footer.png')

    const response = await fetch(`${apiBaseUrl}/api/export-map`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: values.title,
        description: values.description,
        mapImage: mapStore.mapCanvasToDataUrl,
        legendList: legendToPrint,
        logo: logo,
        footer: footer,
        scaleDenominator: mapStore.scaleDenominator,
      }),
    })

    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    window.open(url)

    closeModal()
  },
  () => onInvalidSubmit,
)
</script>
